// src/features/ai/services/geminiService.ts
//
// Production-correct AI service: NO frontend Gemini key. Every call goes
// through the backend proxy, and now uses the unified `apiRequest` HTTP
// client so we get consistent CSRF, trace headers, retry, timeout, and
// error envelopes — same as the rest of the frontend.

import { apiRequest, ApiError, type ApiRequestOptions } from '../../../lib/api';
import { API_PATHS } from '../../../lib/apiRoutes';
import type { Product } from '../../products/types/products.types';
import type { Order } from '../../orders/types/orders.types';
import type { AuditLog } from '../../audits/types/audits.types';
import type { CartItem } from '../../../types';

const DATA_URL_PATTERN = /^data:([a-z]+\/[a-z0-9.+-]+);base64,(.+)$/i;

const normalizeBase64 = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  const normalizedInput = input.replace(/\s+/g, '');
  if (!normalizedInput) return '';

  const dataUrlMatch = normalizedInput.match(DATA_URL_PATTERN);
  if (dataUrlMatch) {
    return dataUrlMatch[2] ?? '';
  }
  return normalizedInput;
};

/**
 * Wrapper that swallows AI errors and returns a `{ ok, data }` discriminated
 * union. AI calls are intentionally non-fatal — we degrade gracefully to a
 * fallback rather than crashing the surrounding feature when the model is
 * slow / down. The underlying `apiRequest` already handles CSRF, trace ids,
 * retries on 5xx, and timeouts.
 */
type SafeAiResultSuccess<T> = { ok: true; data: T; message?: undefined };
type SafeAiResultFailure = { ok: false; data?: undefined; error: ApiError | Error; message: string };
type SafeAiResult<T> = SafeAiResultSuccess<T> | SafeAiResultFailure;

const safeAiRequest = async <T>(
  path: string,
  options: ApiRequestOptions = {},
  defaultTimeoutMs = 20000
): Promise<SafeAiResult<T>> => {
  try {
    const data = await apiRequest<T>(path, {
      timeoutMs: defaultTimeoutMs,
      retries: 0,
      ...options
    });
    return { ok: true, data };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    return { ok: false, error: err, message: err.message };
  }
};

export type ProductScanResult = {
  name: string;
  brand?: string;
  productType?: string;
  category?: string;
  sizeOz: number;
  sizeUnit?: string;
  quantity: number;
  nutritionNote?: string;
  storageZone?: string;
  storageBin?: string;
  image?: string;
  containerType?: string;
  isEligible: boolean;
  message?: string;
};

const emptyProductScanResult = (message: string): ProductScanResult => ({
  name: '',
  sizeOz: 0,
  quantity: 0,
  isEligible: false,
  brand: '',
  productType: '',
  category: '',
  sizeUnit: '',
  nutritionNote: '',
  storageZone: '',
  storageBin: '',
  image: '',
  containerType: '',
  message
});

export const analyzeProductScan = async (
  base64Data: string,
  upc: string,
  mimeType?: string
): Promise<ProductScanResult> => {
  const normalized = normalizeBase64(base64Data);

  if (!normalized) {
    return emptyProductScanResult('No image data provided.');
  }

  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.productScan, {
    method: 'POST',
    body: JSON.stringify({ image: normalized, mimeType, upc }),
    timeoutMs: 20000
  });

  if (!result.ok) {
    return emptyProductScanResult(result.message || 'Product scan unavailable.');
  }

  const data = result.data;
  return {
    name: String(data?.name || ''),
    sizeOz: Number(data?.sizeOz || 0),
    quantity: Number(data?.quantity || 0),
    isEligible: Boolean(data?.isEligible),
    brand: typeof data?.brand === 'string' ? data.brand : '',
    productType: typeof data?.productType === 'string' ? data.productType : '',
    category: typeof data?.category === 'string' ? data.category : '',
    sizeUnit: typeof data?.sizeUnit === 'string' ? data.sizeUnit : '',
    nutritionNote: typeof data?.nutritionNote === 'string' ? data.nutritionNote : '',
    storageZone: typeof data?.storageZone === 'string' ? data.storageZone : '',
    storageBin: typeof data?.storageBin === 'string' ? data.storageBin : '',
    image: typeof data?.image === 'string' ? data.image : '',
    containerType: typeof data?.containerType === 'string' ? data.containerType : '',
    message: typeof data?.message === 'string' ? data.message : undefined
  };
};

const extractTextField = (data: Record<string, unknown> | undefined, fields: string[]): string | null => {
  if (!data) return null;
  for (const field of fields) {
    const value = data[field];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
};

export const getAdvancedInventoryInsights = async (
  inventory: Product[],
  orders: Order[],
  model?: string
): Promise<string> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.inventoryAudit, {
    method: 'POST',
    body: JSON.stringify({ inventory, orders, model }),
    timeoutMs: 20000
  });
  if (!result.ok) return result.message || 'Strategic engine offline: Intelligence Node Unreachable.';
  return extractTextField(result.data, ['insights', 'text']) || 'Audit transmission interrupted.';
};

export const getInventoryInsights = getAdvancedInventoryInsights;

export const getOperationsSummary = async (
  orders: Order[],
  rangeLabel?: string,
  model?: string
): Promise<string> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.opsSummary, {
    method: 'POST',
    body: JSON.stringify({ orders, rangeLabel, model }),
    timeoutMs: 20000
  });
  if (!result.ok) return result.message || 'Operations summary unavailable.';
  return extractTextField(result.data, ['summary', 'text']) || 'Operations summary unavailable.';
};

export const getAuditLogSummary = async (
  auditLogs: AuditLog[],
  model?: string
): Promise<string> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.auditSummary, {
    method: 'POST',
    body: JSON.stringify({ auditLogs, model }),
    timeoutMs: 20000
  });
  if (!result.ok) return result.message || 'Audit log summary unavailable.';
  return extractTextField(result.data, ['summary', 'text']) || 'Audit log summary unavailable.';
};

export const explainDriverIssue = async (
  order: Pick<Order, 'id'> & Partial<Order>,
  errorMessage: string,
  auditLogs?: AuditLog[],
  model?: string
): Promise<string> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.issueExplain, {
    method: 'POST',
    body: JSON.stringify({ order, errorMessage, auditLogs, model }),
    timeoutMs: 20000
  });
  if (!result.ok) return result.message || 'Issue explanation unavailable.';
  return extractTextField(result.data, ['explanation', 'text']) || 'Issue explanation unavailable.';
};

export type AuditModelResponse = {
  models: string[];
  defaultModel?: string;
};

export const getAvailableAuditModels = async (): Promise<AuditModelResponse> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.models, {
    method: 'GET',
    timeoutMs: 10000
  });
  if (!result.ok) return { models: [] };
  const data = result.data;
  return {
    models: Array.isArray(data?.models) ? (data.models as string[]).filter(Boolean) : [],
    defaultModel: typeof data?.defaultModel === 'string' ? data.defaultModel : undefined
  };
};

export type AddressValidationResult = {
  isValid: boolean;
  correctedAddress: string;
  confidence: number;
  issues: string[];
  suggestions?: string;
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip?: string;
};

export const validateAddress = async (
  address: string,
  model?: string
): Promise<AddressValidationResult> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.validateAddress, {
    method: 'POST',
    body: JSON.stringify({ address, model }),
    timeoutMs: 15000
  });
  if (!result.ok) {
    return {
      isValid: false,
      correctedAddress: address,
      confidence: 0,
      issues: [result.message || 'Validation service unavailable'],
      suggestions: 'Please verify your address manually'
    };
  }
  const data = result.data;
  return {
    isValid: Boolean(data?.isValid ?? false),
    correctedAddress: typeof data?.correctedAddress === 'string' ? data.correctedAddress : address,
    confidence: Number(data?.confidence ?? 0),
    issues: Array.isArray(data?.issues) ? (data.issues as string[]) : [],
    suggestions: typeof data?.suggestions === 'string' ? data.suggestions : undefined
  };
};

export type ChatMessage = {
  role: 'user' | 'agent';
  message: string;
  timestamp?: string;
};

export type ChatActionParams = {
  orderId?: string;
  productId?: string;
  category?: string;
  query?: string;
  section?: string;
};

export type ChatUserContext = {
  userId?: string;
  username?: string;
  email?: string;
  role?: string;
  tier?: string;
  activeOrderId?: string;
  cartItemCount?: number;
};

export type ChatAction = {
  label: string;
  action: 'track_order' | 'view_inventory' | 'request_refund' | 'contact_support' | 'view_profile';
  params?: ChatActionParams;
};

export type ChatResponse = {
  reply: string;
  timestamp: string;
  source?: 'account' | 'faq' | 'gemini' | 'escalate';
  escalateSuggested?: boolean;
  actions?: ChatAction[];
};

export const chatWithSupport = async (
  message: string,
  conversationHistory?: ChatMessage[],
  userContext?: ChatUserContext,
  model?: string
): Promise<ChatResponse> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.chat, {
    method: 'POST',
    body: JSON.stringify({
      message,
      conversationHistory,
      userContext,
      model,
      systemPromptExtension: "Include 'actions' array for track_order, view_inventory, etc."
    }),
    timeoutMs: 15000
  });
  if (!result.ok) {
    return {
      reply: "I apologize, but I'm having trouble connecting right now. Please try again or contact support directly.",
      timestamp: new Date().toISOString()
    };
  }
  const data = result.data;
  const replyValue = typeof data?.reply === 'string'
    ? data.reply
    : (typeof data?.data === 'object' && data?.data !== null && typeof (data.data as { response?: unknown }).response === 'string')
      ? (data.data as { response: string }).response
      : 'No response received';
  return {
    reply: replyValue,
    timestamp: typeof data?.timestamp === 'string' ? data.timestamp : new Date().toISOString(),
    source: typeof data?.source === 'string' ? data.source as ChatResponse['source'] : undefined,
    escalateSuggested: data?.escalateSuggested === true,
    actions: Array.isArray(data?.actions) ? data.actions as ChatAction[] : undefined
  };
};

export type ProductCategorization = {
  category: string;
  subcategory: string;
  tags: string[];
  dietaryInfo: string[];
  shelfLife: string;
  storageType: string;
};

const fallbackCategorization: ProductCategorization = {
  category: 'Uncategorized',
  subcategory: '',
  tags: [],
  dietaryInfo: [],
  shelfLife: 'Unknown',
  storageType: 'pantry'
};

export const categorizeProduct = async (
  productName: string,
  brand?: string,
  description?: string,
  image?: string,
  model?: string
): Promise<ProductCategorization> => {
  const normalized = image ? normalizeBase64(image) : undefined;
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.categorizeProduct, {
    method: 'POST',
    body: JSON.stringify({ productName, brand, description, image: normalized, model }),
    timeoutMs: 15000
  });
  if (!result.ok) return fallbackCategorization;
  const data = result.data;
  return {
    category: typeof data?.category === 'string' ? data.category : 'Uncategorized',
    subcategory: typeof data?.subcategory === 'string' ? data.subcategory : '',
    tags: Array.isArray(data?.tags) ? (data.tags as string[]) : [],
    dietaryInfo: Array.isArray(data?.dietaryInfo) ? (data.dietaryInfo as string[]) : [],
    shelfLife: typeof data?.shelfLife === 'string' ? data.shelfLife : 'Unknown',
    storageType: typeof data?.storageType === 'string' ? data.storageType : 'pantry'
  };
};

export type DemandForecastItem = {
  productId: string;
  productName: string;
  predictedSales: number;
  confidence: number;
  trend: string;
  stockRecommendation: string;
};

export type DemandForecastResult = {
  forecast: DemandForecastItem[];
  insights: string;
};

export const getDemandForecast = async (
  products: Product[],
  orderHistory: Order[],
  timeframe?: string,
  model?: string
): Promise<DemandForecastResult> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.demandForecast, {
    method: 'POST',
    body: JSON.stringify({ products, orderHistory, timeframe, model }),
    timeoutMs: 20000
  });
  if (!result.ok) {
    return {
      forecast: [],
      insights: result.message || 'Forecast unavailable'
    };
  }
  const data = result.data;
  return {
    forecast: Array.isArray(data?.forecast) ? (data.forecast as DemandForecastItem[]) : [],
    insights: typeof data?.insights === 'string' ? data.insights : 'No insights available'
  };
};

export type NaturalSearchResult = {
  matchedProducts: string[];
  interpretation: string;
  filters?: {
    priceRange?: { min: number; max: number };
    categories?: string[];
    keywords?: string[];
  };
};

export const naturalLanguageSearch = async (
  query: string,
  products: Product[],
  model?: string
): Promise<NaturalSearchResult> => {
  const result = await safeAiRequest<Record<string, unknown>>(API_PATHS.ai.naturalSearch, {
    method: 'POST',
    body: JSON.stringify({ query, products, model }),
    timeoutMs: 15000
  });
  if (!result.ok) {
    return {
      matchedProducts: [],
      interpretation: result.message || 'Search unavailable'
    };
  }
  const data = result.data;
  return {
    matchedProducts: Array.isArray(data?.matchedProducts) ? (data.matchedProducts as string[]) : [],
    interpretation: typeof data?.interpretation === 'string' ? data.interpretation : 'No matches found',
    filters: data?.filters as NaturalSearchResult['filters']
  };
};
